import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Tooltip,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Search,
  CheckCircle,
  Warning,
  Error,
  ArrowUpward,
  ArrowDownward,
  Close,
  CalendarToday,
  LocalShipping,
  Schedule,
  Download,
  Event
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

// US Federal Holidays for 2024-2026
const US_FEDERAL_HOLIDAYS = [
  // 2024
  { date: '2024-01-01', name: "New Year's Day" },
  { date: '2024-01-15', name: 'Martin Luther King Jr. Day' },
  { date: '2024-02-19', name: "Presidents' Day" },
  { date: '2024-05-27', name: 'Memorial Day' },
  { date: '2024-06-19', name: 'Juneteenth' },
  { date: '2024-07-04', name: 'Independence Day' },
  { date: '2024-09-02', name: 'Labor Day' },
  { date: '2024-10-14', name: 'Columbus Day' },
  { date: '2024-11-11', name: 'Veterans Day' },
  { date: '2024-11-28', name: 'Thanksgiving Day' },
  { date: '2024-12-25', name: 'Christmas Day' },

  // 2025
  { date: '2025-01-01', name: "New Year's Day" },
  { date: '2025-01-20', name: 'Martin Luther King Jr. Day' },
  { date: '2025-02-17', name: "Presidents' Day" },
  { date: '2025-05-26', name: 'Memorial Day' },
  { date: '2025-06-19', name: 'Juneteenth' },
  { date: '2025-07-04', name: 'Independence Day' },
  { date: '2025-09-01', name: 'Labor Day' },
  { date: '2025-10-13', name: 'Columbus Day' },
  { date: '2025-11-11', name: 'Veterans Day' },
  { date: '2025-11-27', name: 'Thanksgiving Day' },
  { date: '2025-12-25', name: 'Christmas Day' },

  // 2026
  { date: '2026-01-01', name: "New Year's Day" },
  { date: '2026-01-19', name: 'Martin Luther King Jr. Day' },
  { date: '2026-02-16', name: "Presidents' Day" },
  { date: '2026-05-25', name: 'Memorial Day' },
  { date: '2026-06-19', name: 'Juneteenth' },
  { date: '2026-07-04', name: 'Independence Day' },
  { date: '2026-09-07', name: 'Labor Day' },
  { date: '2026-10-12', name: 'Columbus Day' },
  { date: '2026-11-11', name: 'Veterans Day' },
  { date: '2026-11-26', name: 'Thanksgiving Day' },
  { date: '2026-12-25', name: 'Christmas Day' }
];

// TIMEZONE UTILITIES - Use EST for business calculations
const TARGET_TIMEZONE = 'America/New_York'; // EST/EDT

// Parse date string to moment object in target timezone
const parseDateToMoment = dateString => {
  if (!dateString) return null;

  // If dateString is already a Date object, convert it
  if (dateString instanceof Date) {
    return dayjs(dateString).tz(TARGET_TIMEZONE);
  }

  // If it's a string, try to parse it with moment
  let m = dayjs.tz(dateString, TARGET_TIMEZONE);

  // If parsing fails, try to parse as ISO string
  if (!m.isValid()) {
    // Try common date formats
    const formats = [
      'YYYY-MM-DD',
      'YYYY/MM/DD',
      'MM/DD/YYYY',
      'MM-DD-YYYY',
      'YYYY-MM-DDTHH:mm:ss',
      'YYYY-MM-DDTHH:mm:ss.SSSZ'
    ];

    for (const format of formats) {
      m = dayjs.tz(dateString, format, TARGET_TIMEZONE);
      if (m.isValid()) break;
    }

    // Last resort: try creating new Date and converting
    if (!m.isValid()) {
      const dateObj = new Date(dateString);
      if (!isNaN(dateObj.getTime())) {
        m = dayjs(dateObj).tz(TARGET_TIMEZONE);
      }
    }
  }

  return m.isValid() ? m : null;
};

// Convert to Date object at start of day in target timezone
const parseDateTimezoneAgnostic = dateString => {
  const m = parseDateToMoment(dateString);
  return m ? m.startOf('day').toDate() : null;
};

const formatDateForDisplay = date => {
  if (!date) return 'N/A';
  return dayjs(date).tz(TARGET_TIMEZONE).format('MMM D, YYYY');
};

// Calculate transit days - BUSINESS DAYS ONLY (exclude weekends/holidays)
const calculateTransitDays = (pickupDateString, deliveryDateString) => {
  const pickup = parseDateToMoment(pickupDateString);
  const delivery = parseDateToMoment(deliveryDateString);

  if (!pickup || !delivery) return null;

  // Start of day for both dates in target timezone
  const pickupStart = pickup.clone().startOf('day');
  const deliveryStart = delivery.clone().startOf('day');

  // If delivery is on or before pickup (same day or earlier), return 0
  if (deliveryStart.isSameOrBefore(pickupStart)) {
    return 0;
  }

  // Count business days between pickup and delivery (EXCLUDING pickup day)
  let businessDays = 0;

  // Start from day after pickup
  let current = pickupStart.clone().add(1, 'day');

  // Keep adding days until we reach or pass delivery date
  while (current.isSameOrBefore(deliveryStart)) {
    if (isBusinessDay(current.toDate())) {
      businessDays++;
    }

    // IMPORTANT: If we found a business day and it's the SAME as delivery date, we're done
    if (current.isSame(deliveryStart, 'day')) {
      break;
    }

    current = current.add(1, 'day');
  }

  return businessDays;
};

// Function to check if a date is a holiday or weekend
const isBusinessDay = date => {
  const m = dayjs(date).tz(TARGET_TIMEZONE);
  const dayOfWeek = m.day();
  const dateStr = m.format('YYYY-MM-DD');

  // Check if weekend (Saturday = 6, Sunday = 0)
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    return false;
  }

  // Check if holiday
  if (US_FEDERAL_HOLIDAYS.some(h => h.date === dateStr)) {
    return false;
  }

  return true;
};

// Function to add business days to a date (using moment only - NO Date methods)
const addBusinessDays = (startDate, daysToAdd) => {
  let current = dayjs(startDate).tz(TARGET_TIMEZONE);
  let daysAdded = 0;

  while (daysAdded < daysToAdd) {
    current = current.add(1, 'day');
    if (isBusinessDay(current.toDate())) {
      daysAdded++;
    }
  }

  return current.toDate();
};

// Function to calculate business days between two dates (using moment only)
const calculateBusinessDays = (startDate, endDate) => {
  let current = dayjs(startDate).tz(TARGET_TIMEZONE).startOf('day');
  const end = dayjs(endDate).tz(TARGET_TIMEZONE).startOf('day');
  let businessDays = 0;

  while (current.isSameOrBefore(end)) {
    if (isBusinessDay(current.toDate())) {
      businessDays++;
    }
    current = current.add(1, 'day');
  }

  return businessDays;
};

// Calculate expected delivery date WITH 1-DAY GRACE PERIOD INCLUDED
const calculateExpectedDelivery = (
  pickupDate,
  transitDays,
  actualPickupDate,
  promisedDeliveryDate
) => {
  const pickupMoment = dayjs(pickupDate).tz(TARGET_TIMEZONE);
  const actualPickupMoment = parseDateToMoment(actualPickupDate);
  const promisedDeliveryMoment = parseDateToMoment(promisedDeliveryDate);

  let adjustedTransitDays = parseInt(transitDays);

  // Check if actual pickup date is same as promised delivery date (late pickup)
  if (actualPickupMoment && promisedDeliveryMoment) {
    const actualPickupStart = actualPickupMoment.clone().startOf('day');
    const promisedDeliveryStart = promisedDeliveryMoment.clone().startOf('day');

    if (actualPickupStart.isSame(promisedDeliveryStart, 'day')) {
      // Late pickup - add 1 business day penalty
      adjustedTransitDays += 1;
    }
  }

  // First, add business days for transit (with possible penalty)
  const transitEndDate = addBusinessDays(
    pickupMoment.toDate(),
    adjustedTransitDays
  );

  // Then, add 1 MORE BUSINESS DAY for grace period
  const expectedWithGrace = addBusinessDays(transitEndDate, 1);

  return dayjs(expectedWithGrace).tz(TARGET_TIMEZONE);
};

// Get holidays for a specific year
const getHolidaysByYear = year => {
  return US_FEDERAL_HOLIDAYS.filter(holiday => {
    const holidayYear = holiday.date.split('-')[0];
    return holidayYear === year.toString();
  });
};

const OnTimeDeliveryTab = ({ shipments = [], lateShipsCol, viewDetails }) => {
  const [selectedShipmentType, setSelectedShipmentType] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCarrier, setSelectedCarrier] = useState(null);
  const [lateShipmentsDialogOpen, setLateShipmentsDialogOpen] = useState(false);
  const [holidayCalendarOpen, setHolidayCalendarOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState('2026');

  // Sorting state for main table
  const [sortColumn, setSortColumn] = useState('totalShipments');
  const [sortDirection, setSortDirection] = useState('desc');

  // Sorting state for late shipments dialog
  const [lateSortColumn, setLateSortColumn] = useState('daysLate');
  const [lateSortDirection, setLateSortDirection] = useState('desc');

  // Handle column sorting for main table
  const handleSort = column => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Handle sorting for late shipments table
  const handleLateShipmentSort = column => {
    if (lateSortColumn === column) {
      setLateSortDirection(lateSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setLateSortColumn(column);
      setLateSortDirection('desc');
    }
  };

  // Get sort icon for main table
  const getSortIcon = column => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? (
      <ArrowUpward fontSize='small' />
    ) : (
      <ArrowDownward fontSize='small' />
    );
  };

  // Get sort icon for late shipments table
  const getLateSortIcon = column => {
    if (lateSortColumn !== column) return null;
    return lateSortDirection === 'asc' ? (
      <ArrowUpward fontSize='small' />
    ) : (
      <ArrowDownward fontSize='small' />
    );
  };

  // Export main table to Excel
  const exportMainTableToExcel = () => {
    if (!calculateOnTimeDelivery.carrierData.length) {
      alert('No data to export!');
      return;
    }

    try {
      // Prepare data for export
      const exportData = calculateOnTimeDelivery.carrierData.map(carrier => ({
        'Carrier Name': carrier.carrierName,
        'Total Shipments': carrier.totalShipments,
        '% of Total': carrier.percentOfTotal,
        'Late Shipments': carrier.lateShipments,
        'On-Time Percentage': carrier.onTimePercentage,
        'Average Late Days': carrier.avgLateDays,
        'Grace Period Used': carrier.gracePeriodUsed,
        'Grace Period %': carrier.gracePeriodPercent,
        'Early Deliveries': carrier.earlyDeliveries,
        'On-Time Shipments': carrier.onTimeShipments,
        'Avg Actual Transit Days': carrier.avgActualTransitDays,
        'Avg Expected Transit Days': carrier.avgExpectedTransitDays,
        Status:
          carrier.status === 'green'
            ? 'Excellent (95-100%)'
            : carrier.status === 'yellow'
              ? 'Good (80-94%)'
              : 'Poor (<80%)'
      }));

      // Add summary row if available
      if (calculateOnTimeDelivery.summary) {
        const summary = calculateOnTimeDelivery.summary;
        exportData.push({});
        exportData.push({
          'Carrier Name': 'SUMMARY',
          'Total Shipments': summary.totalShipments,
          '% of Total': '100%',
          'Late Shipments': summary.totalLateShipments,
          'On-Time Percentage': `${summary.onTimePercentage}%`,
          'Average Late Days': '-',
          'Grace Period Used': summary.totalGracePeriodUsed,
          'Grace Period %': `${(
            (summary.totalGracePeriodUsed / summary.totalShipments) *
            100
          ).toFixed(1)}%`,
          'Early Deliveries': summary.totalEarlyDeliveries,
          'On-Time Shipments': summary.totalOnTimeShipments,
          'Avg Actual Transit Days': '-',
          'Avg Expected Transit Days': '-',
          Status: '-'
        });
      }

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      const wscols = [
        { wch: 25 }, // Carrier Name
        { wch: 15 }, // Total Shipments
        { wch: 12 }, // % of Total
        { wch: 15 }, // Late Shipments
        { wch: 18 }, // On-Time Percentage
        { wch: 18 }, // Average Late Days
        { wch: 18 }, // Grace Period Used
        { wch: 15 }, // Grace Period %
        { wch: 15 }, // Early Deliveries
        { wch: 18 }, // On-Time Shipments
        { wch: 20 }, // Avg Actual Transit Days
        { wch: 20 }, // Avg Expected Transit Days
        { wch: 20 } // Status
      ];
      ws['!cols'] = wscols;

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Carrier Performance');

      // Add metadata sheet
      const metadata = [
        ['Report: On-Time Delivery Performance'],
        ['Generated: ', new Date().toLocaleString()],
        ['Shipment Type: ', selectedShipmentType],
        ['Search Term: ', searchTerm || 'None'],
        ['Total Carriers: ', calculateOnTimeDelivery.carrierData.length],
        ['', ''],
        ['CALCULATION RULES:'],
        ['• Transit days = Business Days only (exclude weekends & holidays)'],
        ['• Expected delivery includes 1 calendar day grace period'],
        ['• On-time = Delivery on or before (Expected Date + Grace Period)'],
        ['• Late = Delivery after (Expected Date + Grace Period)'],
        ['• All times in EST/EDT (America/New_York)'],
        ['', ''],
        ...(calculateOnTimeDelivery.summary
          ? [
              [
                'Overall On-Time %: ',
                `${calculateOnTimeDelivery.summary.onTimePercentage}%`
              ],
              [
                'Total Shipments: ',
                calculateOnTimeDelivery.summary.totalShipments
              ],
              [
                'On-Time Shipments: ',
                calculateOnTimeDelivery.summary.totalOnTimeShipments
              ],
              [
                'Late Shipments: ',
                calculateOnTimeDelivery.summary.totalLateShipments
              ],
              [
                'Grace Period Used: ',
                calculateOnTimeDelivery.summary.totalGracePeriodUsed
              ],
              [
                'Early Deliveries: ',
                calculateOnTimeDelivery.summary.totalEarlyDeliveries
              ]
            ]
          : [])
      ];

      const wsMetadata = XLSX.utils.aoa_to_sheet(metadata);
      XLSX.utils.book_append_sheet(wb, wsMetadata, 'Report Info');

      // Generate Excel file
      const excelBuffer = XLSX.write(wb, {
        bookType: 'xlsx',
        type: 'array'
      });

      const data = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
      });

      // Save file
      const fileName = `Carrier_Performance_${selectedShipmentType}_${
        new Date().toISOString().split('T')[0]
      }.xlsx`;
      saveAs(data, fileName);
    } catch (error) {
      console.error('Export error:', error);
      alert('Error exporting to Excel. Please try again.');
    }
  };

  // Export late shipments to Excel
  const exportLateShipmentsToExcel = (carrierName, shipments) => {
    if (!carrierName || !shipments || shipments.length === 0) {
      alert('No late shipments data to export!');
      return;
    }

    try {
      // Prepare data for export
      const exportData = shipments.map((shipment, index) => ({
        '#': index + 1,
        'Shipment ID': shipment.shipmentId || `SHIP-${index + 1}`,
        'Pro Number': shipment.proNumber || 'N/A',
        'BOL Number': shipment.bolNumber || 'N/A',
        'Est Transit Days': shipment.estimatedTransitDays,
        'Promised Date': shipment.formattedPromisedDeliveryDate,
        'Pickup Date': shipment.formattedPickupDate,
        'Actual Delivery': shipment.formattedActualDate,
        'Expected Delivery': shipment.formattedExpectedDate,
        'Transit Days (Actual)': shipment.actualTransitDays,
        'Transit Days (Expected)': shipment.expectedTransitDays,
        'Days Late': shipment.daysLateBeyondGrace,
        'Hours Late': Math.round(shipment.diffHours),
        'Delay Severity':
          shipment.daysLateBeyondGrace > 7
            ? 'Significant (>7 days)'
            : shipment.daysLateBeyondGrace > 3
              ? 'Moderate (4-7 days)'
              : 'Minor (1-3 days)',
        Carrier: carrierName,
        'Shipment Type': shipment.shipmentType || 'Standard',
        Notes: shipment.notes || ''
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      const wscols = [
        { wch: 8 }, // #
        { wch: 15 }, // Shipment ID
        { wch: 15 }, // bolNumber
        { wch: 20 }, // Customer
        { wch: 15 }, // Promised Date
        { wch: 15 }, // Pickup Date
        { wch: 18 }, // Actual Delivery
        { wch: 18 }, // Expected Delivery
        { wch: 18 }, // Transit Days (Actual)
        { wch: 18 }, // Transit Days (Expected)
        { wch: 12 }, // Days Late
        { wch: 12 }, // Hours Late
        { wch: 18 }, // Delay Severity
        { wch: 20 }, // Carrier
        { wch: 15 }, // Shipment Type
        { wch: 30 } // Notes
      ];
      ws['!cols'] = wscols;

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Late Shipments');

      // Add summary sheet
      const totalDaysLate = shipments.reduce(
        (sum, s) => sum + s.daysLateBeyondGrace,
        0
      );
      const avgDaysLate = (totalDaysLate / shipments.length).toFixed(1);
      const maxDaysLate = Math.max(
        ...shipments.map(s => s.daysLateBeyondGrace)
      );

      const summaryData = [
        ['Late Shipments Summary'],
        [''],
        ['Carrier:', carrierName],
        ['Generated:', new Date().toLocaleString()],
        ['Total Late Shipments:', shipments.length],
        ['Total Late Days:', totalDaysLate],
        ['Average Days Late:', avgDaysLate],
        ['Maximum Days Late:', maxDaysLate],
        [''],
        ['Calculation Rules:'],
        ['• Transit days = Business Days only (exclude weekends & holidays)'],
        ['• Expected delivery includes 1 calendar day grace period'],
        ['• Late = Delivery after (Expected Date + Grace Period)'],
        ['• All times in EST/EDT (America/New_York)'],
        [''],
        [
          'This report shows proof of late deliveries for carrier performance review.'
        ]
      ];

      const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

      // Generate Excel file
      const excelBuffer = XLSX.write(wb, {
        bookType: 'xlsx',
        type: 'array'
      });

      const data = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
      });

      // Save file
      const fileName = `Late_Shipments_${carrierName.replace(
        /[^a-z0-9]/gi,
        '_'
      )}_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(data, fileName);
    } catch (error) {
      console.error('Export error:', error);
      alert('Error exporting to Excel. Please try again.');
    }
  };

  // Function to get detailed late shipments for a carrier
  const getLateShipmentsDetails = carrierName => {
    // Get the carrier data from calculateOnTimeDelivery
    const carrierData = calculateOnTimeDelivery.carrierData.find(
      c => c.carrierName === carrierName
    );

    if (!carrierData) return [];

    // Use the same shipments that were processed in main calculation
    const carrierShipments = carrierData.shipmentData || [];

    // Process each shipment with EXACT SAME LOGIC as main calculation
    const allShipments = carrierShipments
      .map(shipment => {
        const pickupMoment = parseDateToMoment(shipment.actualPickupDate);
        const deliveryMoment = parseDateToMoment(shipment.actualDeliveryDate);
        const promisedDeliveryMoment = parseDateToMoment(
          shipment.promisedDeliveryDate
        );

        if (!pickupMoment || !deliveryMoment) return null;

        // Calculate actual transit days (BUSINESS DAYS ONLY)
        const actualTransitDays = calculateTransitDays(
          shipment.actualPickupDate,
          shipment.actualDeliveryDate
        );

        // Get expected transit days (business days from promised date)
        const expectedTransitDays = shipment.estimatedTransitDays;

        // 👇 UPDATED: Calculate expected delivery date WITH GRACE PERIOD AND LATE PICKUP PENALTY
        const expectedDeliveryMoment = calculateExpectedDelivery(
          pickupMoment.toDate(),
          parseInt(expectedTransitDays),
          shipment.actualPickupDate,
          shipment.promisedDeliveryDate
        );

        // 👇 NEW: Check for late pickup
        const actualPickupStart = pickupMoment.clone().startOf('day');
        const promisedDeliveryStart = promisedDeliveryMoment
                    .startOf('day');
        const isLatePickup = actualPickupStart.isSame(
          promisedDeliveryStart,
          'day'
        );

        // Compare dates (start of day for fair comparison)
        const expectedDate = expectedDeliveryMoment.clone().startOf('day');
        const actualDate = deliveryMoment.clone().startOf('day');

        // Determine if late, on-time, or early
        let isLate = false;
        let usedGracePeriod = false;
        let daysLateBeyondGrace = 0;

        const transitDiff =
          actualTransitDays -
          (parseInt(expectedTransitDays) + (isLatePickup ? 1 : 0));

        if (transitDiff > 0 && actualDate.isAfter(expectedDate)) {
          // LATE: Actual delivery is after expected date + grace period
          isLate = true;
          daysLateBeyondGrace = calculateBusinessDays(
            expectedDate.toDate(),
            actualDate.toDate()
          );
        } else if (actualDate.isSame(expectedDate, 'day')) {
          // ON TIME: Delivered on expected date + grace period
          usedGracePeriod = true;
          isLate = false;
        }

        return {
          ...shipment,
          pickupMoment,
          deliveryMoment,
          expectedDeliveryMoment,
          expectedTransitDays: parseInt(expectedTransitDays),
          actualTransitDays,
          diffHours: deliveryMoment.diff(expectedDeliveryMoment, 'hours', true),
          daysLateBeyondGrace,
          isLate,
          usedGracePeriod,
          isLatePickup, // 👈 NEW
          formattedPickupDate: formatDateForDisplay(pickupMoment.toDate()),
          formattedActualDate: formatDateForDisplay(deliveryMoment.toDate()),
          formattedPromisedDeliveryDate: formatDateForDisplay(
            promisedDeliveryMoment.toDate()
          ),
          formattedExpectedDate: formatDateForDisplay(
            expectedDeliveryMoment.toDate()
          )
        };
      })
      .filter(shipment => shipment !== null);

    // CRITICAL FIX: Only return shipments where isLate === true
    const lateShipments = allShipments.filter(
      shipment => shipment.isLate === true
    );

    return lateShipments;
  };

  // Function to handle opening late shipments dialog
  const handleViewLateShipments = carrierName => {
    setSelectedCarrier(carrierName);
    setLateShipmentsDialogOpen(true);
  };

  // Calculate On-Time Delivery performance with CORRECT business days and grace period
  const calculateOnTimeDelivery = useMemo(() => {
    if (!shipments.length) return { carrierData: [], summary: null };

    // 1️⃣ Filter by shipment type
    const typeFiltered =
      selectedShipmentType === 'All'
        ? shipments
        : shipments.filter(s => s.shipmentType === selectedShipmentType);

    // 2️⃣ FILTER: Only delivered shipments with actual dates
    const validShipments = typeFiltered.filter(s => {
      // Basic checks
      if (s.shipmentStatus !== 'Delivered') return false;
      if (!s.actualDeliveryDate || !s.actualPickupDate) return false;

      // Parse dates using timezone-agnostic function
      const pickupMoment = parseDateToMoment(s.actualPickupDate);
      const deliveryMoment = parseDateToMoment(s.actualDeliveryDate);

      // Validate dates
      if (!pickupMoment || !deliveryMoment) return false;

      // Ensure delivery is not before pickup (data validation)
      return deliveryMoment.isSameOrAfter(pickupMoment);
    });

    const carrierGroups = {};

    // 3️⃣ LOGIC: Use EXACT SAME LOGIC as getLateShipmentsDetails
    validShipments.forEach(shipment => {
      const carrierName = shipment.carrierName;

      if (!carrierGroups[carrierName]) {
        carrierGroups[carrierName] = {
          totalShipments: 0,
          lateShipments: 0,
          onTimeShipments: 0,
          earlyDeliveries: 0,
          gracePeriodUsed: 0,
          totalLateDays: 0,
          totalActualTransitDays: 0,
          totalExpectedTransitDays: 0,
          latePickupCount: 0 // 👈 NEW: Track late pickups
        };
      }

      carrierGroups[carrierName].totalShipments++;

      // Parse dates
      const pickupMoment = parseDateToMoment(shipment.actualPickupDate);
      const deliveryMoment = parseDateToMoment(shipment.actualDeliveryDate);

      if (!pickupMoment || !deliveryMoment) return;

      // Calculate actual transit days (BUSINESS DAYS ONLY)
      const actualTransitDays = calculateTransitDays(
        shipment.actualPickupDate,
        shipment.actualDeliveryDate
      );

      carrierGroups[carrierName].totalActualTransitDays += actualTransitDays;

      // Get expected transit days (business days from promised date)
      const expectedTransitDays = shipment.estimatedTransitDays;

      const actualPickupMoment = parseDateToMoment(shipment.actualPickupDate);
      const promisedDeliveryMoment = parseDateToMoment(
        shipment.promisedDeliveryDate
      );

      if (actualPickupMoment && promisedDeliveryMoment) {
        const actualPickupStart = actualPickupMoment.clone().startOf('day');
        const promisedDeliveryStart = promisedDeliveryMoment
                    .startOf('day');

        if (actualPickupStart.isSame(promisedDeliveryStart, 'day')) {
          carrierGroups[carrierName].latePickupCount++;
        }
      }
      carrierGroups[carrierName].totalExpectedTransitDays +=
        parseInt(expectedTransitDays) +
        (actualPickupMoment &&
        promisedDeliveryMoment &&
        actualPickupMoment
                    .startOf('day')
          .isSame(promisedDeliveryMoment.clone().startOf('day'), 'day')
          ? 1
          : 0);

      // Calculate expected delivery date WITH GRACE PERIOD INCLUDED
      const expectedDeliveryMoment = calculateExpectedDelivery(
        pickupMoment.toDate(),
        parseInt(expectedTransitDays),
        shipment.actualPickupDate,
        shipment.promisedDeliveryDate
      );

      // Compare dates (start of day for fair comparison)
      const expectedDate = expectedDeliveryMoment.clone().startOf('day');
      const actualDate = deliveryMoment.clone().startOf('day');
      const transitDiff = actualTransitDays - parseInt(expectedTransitDays);
      // Determine if late, on-time, or early using moment's date comparisons
      if (transitDiff > 0 && actualDate.isAfter(expectedDate)) {
        // LATE
        const businessDaysLate = calculateBusinessDays(
          expectedDate.toDate(),
          actualDate.toDate()
        );

        carrierGroups[carrierName].lateShipments++;
        carrierGroups[carrierName].totalLateDays += businessDaysLate;
      } else {
        // ON TIME (includes SAME DAY + grace + early)
        carrierGroups[carrierName].onTimeShipments++;

        if (actualDate.isSame(expectedDate, 'day')) {
          carrierGroups[carrierName].gracePeriodUsed++;
        } else if (transitDiff < 0) {
          carrierGroups[carrierName].earlyDeliveries++;
        }
      }
      // else {
      //         // LATE: Delivered after expected date + grace period (business days)
      //         carrierGroups[carrierName].lateShipments++;
      //         carrierGroups[carrierName].totalLateDays += calendarDaysDiff;
      //       }
    });

    // 4️⃣ Totals (using same logic as dialog)
    const totalShipments = validShipments.length;
    const totalLateShipments = Object.values(carrierGroups).reduce(
      (sum, c) => sum + c.lateShipments,
      0
    );
    const totalOnTimeShipments = Object.values(carrierGroups).reduce(
      (sum, c) => sum + c.onTimeShipments,
      0
    );
    const totalGracePeriodUsed = Object.values(carrierGroups).reduce(
      (sum, c) => sum + c.gracePeriodUsed,
      0
    );

    const totalEarlyDeliveries = Object.values(carrierGroups).reduce(
      (sum, c) => sum + c.earlyDeliveries,
      0
    );
    const totalLatePickups = Object.values(carrierGroups).reduce(
      (sum, c) => sum + (c.latePickupCount || 0),
      0
    );

    const onTimePercentage =
      totalShipments > 0
        ? ((totalOnTimeShipments / totalShipments) * 100).toFixed(1)
        : 0;

    // 5️⃣ Prepare carrier rows
    let allCarrierData = Object.entries(carrierGroups).map(
      ([carrierName, data]) => {
        const percentOfTotal =
          totalShipments > 0
            ? ((data.totalShipments / totalShipments) * 100).toFixed(1)
            : 0;

        const onTimePercent =
          data.totalShipments > 0
            ? ((data.onTimeShipments / data.totalShipments) * 100).toFixed(1)
            : 0;

        const avgLateDays =
          data.lateShipments > 0
            ? (data.totalLateDays / data.lateShipments).toFixed(0)
            : 0;

        const gracePeriodPercent =
          data.totalShipments > 0
            ? ((data.gracePeriodUsed / data.totalShipments) * 100).toFixed(1)
            : 0;

        const avgActualTransitDays =
          data.totalShipments > 0
            ? (data.totalActualTransitDays / data.totalShipments).toFixed(1)
            : 0;

        const avgExpectedTransitDays =
          data.totalShipments > 0
            ? (data.totalExpectedTransitDays / data.totalShipments).toFixed(1)
            : 0;

        // Status logic
        let status = 'red';
        let statusColor = '#ffebee';
        let icon = <Error sx={{ color: '#f44336' }} />;

        const onTimePercentNum = parseFloat(onTimePercent);
        if (onTimePercentNum >= 95) {
          status = 'green';
          statusColor = '#e8f5e9';
          icon = <CheckCircle sx={{ color: '#4caf50' }} />;
        } else if (onTimePercentNum >= 80) {
          status = 'yellow';
          statusColor = '#fffde7';
          icon = <Warning sx={{ color: '#ff9800' }} />;
        }
        const latePickupPercent =
          data.totalShipments > 0
            ? (
                ((data.latePickupCount || 0) / data.totalShipments) *
                100
              ).toFixed(1)
            : 0;
        return {
          carrierName,
          totalShipments: data.totalShipments,
          percentOfTotal: `${percentOfTotal}%`,
          rawPercentOfTotal: parseFloat(percentOfTotal),
          lateShipments: data.lateShipments || 0,
          onTimePercentage: `${onTimePercent}%`,
          rawOnTimePercent: onTimePercentNum,
          avgLateDays,
          rawAvgLateDays: parseFloat(avgLateDays),
          gracePeriodUsed: data.gracePeriodUsed,
          gracePeriodPercent: `${gracePeriodPercent}%`,
          earlyDeliveries: data.earlyDeliveries,
          avgActualTransitDays,
          avgExpectedTransitDays,
          latePickupCount: data.latePickupCount || 0, // 👈 NEW
          latePickupPercent: `${latePickupPercent}%`, // 👈 NEW
          status,
          statusColor,
          icon,
          onTimeShipments: data.onTimeShipments,
          shipmentData: validShipments.filter(
            s => s.carrierName === carrierName
          )
        };
      }
    );

    // 6️⃣ Sorting
    allCarrierData.sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'carrierName':
          aValue = a.carrierName.toLowerCase();
          bValue = b.carrierName.toLowerCase();
          break;
        case 'latePickupCount':
          aValue = a.latePickupCount;
          bValue = b.latePickupCount;
          break;
        case 'totalShipments':
          aValue = a.totalShipments;
          bValue = b.totalShipments;
          break;
        case 'percentOfTotal':
          aValue = a.rawPercentOfTotal;
          bValue = b.rawPercentOfTotal;
          break;
        case 'lateShipments':
          aValue = a.lateShipments;
          bValue = b.lateShipments;
          break;
        case 'onTimePercentage':
          aValue = a.rawOnTimePercent;
          bValue = b.rawOnTimePercent;
          break;
        case 'avgLateDays':
          aValue = a.rawAvgLateDays;
          bValue = b.rawAvgLateDays;
          break;
        case 'gracePeriodUsed':
          aValue = a.gracePeriodUsed;
          bValue = b.gracePeriodUsed;
          break;
        case 'avgActualTransitDays':
          aValue = parseFloat(a.avgActualTransitDays);
          bValue = parseFloat(b.avgActualTransitDays);
          break;
        case 'avgExpectedTransitDays':
          aValue = parseFloat(a.avgExpectedTransitDays);
          bValue = parseFloat(b.avgExpectedTransitDays);
          break;
        default:
          aValue = a.totalShipments;
          bValue = b.totalShipments;
      }

      return sortDirection === 'asc'
        ? aValue > bValue
          ? 1
          : -1
        : aValue < bValue
          ? 1
          : -1;
    });

    // 7️⃣ Search filter
    const filteredData = searchTerm
      ? allCarrierData.filter(c =>
          c.carrierName.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : allCarrierData;

    return {
      carrierData: filteredData,
      summary: {
        totalShipments,
        totalLateShipments,
        totalOnTimeShipments,
        totalGracePeriodUsed,
        totalEarlyDeliveries,
        totalLatePickups,
        shipsWithMissingDates: typeFiltered.length - validShipments.length,
        onTimePercentage: parseFloat(onTimePercentage),
        selectedShipmentType
      }
    };
  }, [shipments, selectedShipmentType, searchTerm, sortColumn, sortDirection]);

  // Holiday Calendar Dialog
  const HolidayCalendarDialog = () => {
    const holidays = getHolidaysByYear(selectedYear);

    const formatDate = dateString => {
      return dayjs(dateString)
        .tz(TARGET_TIMEZONE)
        .format('dddd, MMMM D, YYYY');
    };

    return (
      <Dialog
        open={holidayCalendarOpen}
        onClose={() => setHolidayCalendarOpen(false)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Event />
            <Typography variant='h6'>US Federal Holidays Calendar</Typography>
            <Chip
              label={`${selectedYear} (${holidays.length} holidays)`}
              color='primary'
              size='small'
            />
          </Box>
          <IconButton
            aria-label='close'
            onClick={() => setHolidayCalendarOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme => theme.palette.grey[500]
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Year Selection */}
          <Box sx={{ mb: 3 }}>
            <Typography variant='subtitle2' gutterBottom>
              Select Year:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {['2024', '2025', '2026'].map(year => (
                <Chip
                  key={year}
                  label={year}
                  onClick={() => setSelectedYear(year)}
                  color={selectedYear === year ? 'primary' : 'default'}
                  variant={selectedYear === year ? 'filled' : 'outlined'}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>

          {/* Holiday List */}
          <Paper elevation={1} sx={{ mb: 2 }}>
            <Box sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
              <Typography variant='subtitle2' gutterBottom>
                Business Day Calculation Rules (EST/EDT Timezone):
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                • All calculations use America/New_York timezone (EST/EDT)
                <br />
                • Transit days = BUSINESS DAYS only (exclude weekends &
                holidays)
                <br />
                • Expected delivery includes 1 calendar day grace period
                <br />
                • On-time = Delivery on or before (Expected Date + Grace Period)
                <br />
                • Late = Delivery after (Expected Date + Grace Period)
                <br />• Same day pickup/delivery = 0 transit days
              </Typography>
            </Box>
          </Paper>

          <List>
            {holidays.map((holiday, index) => (
              <ListItem
                key={holiday.date}
                divider={index < holidays.length - 1}
                sx={{
                  backgroundColor: index % 2 === 0 ? '#fff' : '#fafafa',
                  '&:hover': { backgroundColor: '#f0f0f0' }
                }}
              >
                <ListItemIcon>
                  <CalendarToday color='primary' />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant='body1' fontWeight='500'>
                      {holiday.name}
                    </Typography>
                  }
                  secondary={
                    <Typography variant='body2' color='textSecondary'>
                      {formatDate(holiday.date)}
                    </Typography>
                  }
                />
                <Chip
                  label='Non-Business Day'
                  size='small'
                  color='warning'
                  variant='outlined'
                />
              </ListItem>
            ))}
          </List>

          {/* Important Dates for 2026 */}
          {selectedYear === '2026' && (
            <Paper sx={{ p: 2, mt: 2, backgroundColor: '#fff8e1' }}>
              <Typography variant='subtitle2' gutterBottom color='#ed6c02'>
                ⚠️ Important for January 2026 Shipments:
              </Typography>
              <Typography variant='body2'>
                • <strong>January 1, 2026 (Thursday)</strong>: New Year's Day -
                NON-BUSINESS DAY
                <br />• <strong>January 19, 2026 (Monday)</strong>: MLK Day -
                NON-BUSINESS DAY
                <br />
                • Shipments promised before these dates will have transit days
                extended
                <br />• Check late shipments for deliveries affected by these
                holidays
              </Typography>
            </Paper>
          )}

          {/* Summary */}
          <Box
            sx={{ mt: 3, p: 2, backgroundColor: '#e8f5e9', borderRadius: 1 }}
          >
            <Typography variant='body2'>
              <strong>Total Non-Business Days in {selectedYear}:</strong>{' '}
              {holidays.length} holidays + 104 weekend days ={' '}
              {holidays.length + 104} days
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setHolidayCalendarOpen(false)}>Close</Button>
          <Button
            variant='outlined'
            color='primary'
            onClick={() => {
              const yearHolidays = getHolidaysByYear(selectedYear);
              const exportData = yearHolidays.map((holiday, index) => ({
                '#': index + 1,
                'Holiday Name': holiday.name,
                Date: formatDate(holiday.date),
                Year: selectedYear,
                Type: 'US Federal Holiday',
                'Business Day': 'NO',
                Notes: 'Non-working day for transit calculations'
              }));

              const ws = XLSX.utils.json_to_sheet(exportData);
              const wb = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(wb, ws, 'Holidays');

              const excelBuffer = XLSX.write(wb, {
                bookType: 'xlsx',
                type: 'array'
              });
              const data = new Blob([excelBuffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              });

              saveAs(data, `US_Federal_Holidays_${selectedYear}.xlsx`);
            }}
            startIcon={<Download />}
          >
            Export Holidays
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Late Shipments Dialog Component
  const LateShipmentsDialog = () => {
    if (!selectedCarrier) return null;

    const lateShipments = getLateShipmentsDetails(selectedCarrier);
    const carrierData = calculateOnTimeDelivery.carrierData.find(
      c => c.carrierName === selectedCarrier
    );

    // Sort late shipments
    const sortedLateShipments = [...lateShipments].sort((a, b) => {
      let aValue, bValue;

      switch (lateSortColumn) {
        case 'shipmentId':
          aValue = a.shipmentId || '';
          bValue = b.shipmentId || '';
          break;
        case 'proNumber':
          aValue = a.proNumber || '';
          bValue = b.proNumber || '';
          break;
        case 'pickupDate':
          aValue = a.pickupMoment.valueOf();
          bValue = b.pickupMoment.valueOf();
          break;
        case 'actualDate':
          aValue = a.deliveryMoment.valueOf();
          bValue = b.deliveryMoment.valueOf();
          break;
        case 'daysLate':
          aValue = a.daysLateBeyondGrace;
          bValue = b.daysLateBeyondGrace;
          break;
        case 'transitDays':
          aValue = a.actualTransitDays;
          bValue = b.actualTransitDays;
          break;
        case 'hoursLate':
          aValue = a.diffHours;
          bValue = b.diffHours;
          break;
        default:
          aValue = a.daysLateBeyondGrace;
          bValue = b.daysLateBeyondGrace;
      }

      if (lateSortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return (
      <Dialog
        open={lateShipmentsDialogOpen}
        onClose={() => setLateShipmentsDialogOpen(false)}
        maxWidth='xl'
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LocalShipping />
            <Typography variant='h6'>
              Late Shipments Details - {selectedCarrier}
            </Typography>
            <Chip
              label={`${carrierData?.lateShipments || 0} Late Shipments`}
              color='error'
              size='small'
            />
          </Box>
          <IconButton
            aria-label='close'
            onClick={() => setLateShipmentsDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme => theme.palette.grey[500]
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Carrier Summary */}
          <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f8f9fa' }}>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant='caption' color='textSecondary'>
                  Total Shipments
                </Typography>
                <Typography variant='h6'>
                  {carrierData?.totalShipments || 0}
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' color='textSecondary'>
                  On-Time Percentage
                </Typography>
                <Typography
                  variant='h6'
                  color={
                    carrierData?.rawOnTimePercent >= 95
                      ? 'success.main'
                      : carrierData?.rawOnTimePercent >= 80
                        ? 'warning.main'
                        : 'error.main'
                  }
                >
                  {carrierData?.onTimePercentage || '0%'}
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' color='textSecondary'>
                  Average Late Days
                </Typography>
                <Typography variant='h6'>
                  {carrierData?.avgLateDays || 0} days
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' color='textSecondary'>
                  Avg Transit Days (Actual)
                </Typography>
                <Typography variant='h6'>
                  {carrierData?.avgActualTransitDays || 0} days
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' color='textSecondary'>
                  Avg Transit Days (Expected)
                </Typography>
                <Typography variant='h6'>
                  {carrierData?.avgExpectedTransitDays || 0} days
                </Typography>
              </Box>
              <Box>
                <Button
                  size='small'
                  variant='outlined'
                  startIcon={<Event />}
                  onClick={() => setHolidayCalendarOpen(true)}
                  sx={{ mt: 0.5 }}
                >
                  View Holiday Calendar
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Detailed Late Shipments Table with Sorting */}
          {sortedLateShipments.length > 0 ? (
            <>
              <TableContainer component={Paper} variant='outlined'>
                <Table size='small'>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      {/* Shipment ID with Sorting */}
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#e0e0e0' }
                        }}
                        onClick={() => handleLateShipmentSort('shipmentId')}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          Shipment ID
                          {getLateSortIcon('shipmentId')}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#e0e0e0' }
                        }}
                        onClick={() => handleLateShipmentSort('bolNumber')}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          BOL Number
                          {getLateSortIcon('bolNumber')}
                        </Box>
                      </TableCell>

                      {/* Customer with Sorting */}
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#e0e0e0' }
                        }}
                        onClick={() => handleLateShipmentSort('proNumber')}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          PRO#
                          {getLateSortIcon('proNumber')}
                        </Box>
                      </TableCell>

                      {/* Pickup Date with Sorting */}
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#e0e0e0' }
                        }}
                        onClick={() => handleLateShipmentSort('pickupDate')}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          <CalendarToday fontSize='small' />
                          Pickup
                          {getLateSortIcon('pickupDate')}
                        </Box>
                      </TableCell>

                      {/* Actual Delivery with Sorting */}
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#e0e0e0' }
                        }}
                        onClick={() => handleLateShipmentSort('actualDate')}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          <CalendarToday fontSize='small' />
                          Actual
                          {getLateSortIcon('actualDate')}
                        </Box>
                      </TableCell>

                      {/* Transit Days with Sorting */}
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#e0e0e0' }
                        }}
                        onClick={() => handleLateShipmentSort('transitDays')}
                        align='center'
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            justifyContent: 'center'
                          }}
                        >
                          <Schedule fontSize='small' />
                          Transit Days
                          {getLateSortIcon('transitDays')}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#e0e0e0' }
                        }}
                        onClick={() =>
                          handleLateShipmentSort('estimatedTransitDays')
                        }
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          Est Transit Days
                          {getLateSortIcon('estimatedTransitDays')}
                        </Box>
                      </TableCell>

                      {/* Days Late with Sorting */}
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#e0e0e0' }
                        }}
                        onClick={() => handleLateShipmentSort('daysLate')}
                        align='right'
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            justifyContent: 'flex-end'
                          }}
                        >
                          <Schedule fontSize='small' />
                          Days Late
                          {getLateSortIcon('daysLate')}
                        </Box>
                      </TableCell>

                      {/* Expected vs Actual */}
                      <TableCell
                        sx={{
                          fontWeight: 'bold'
                        }}
                        align='center'
                      >
                        Expected vs Actual
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedLateShipments.map((shipment, index) => (
                      <TableRow
                        key={`${shipment.shipmentId}-${index}`}
                        sx={{
                          '&:hover': { backgroundColor: '#f9f9f9' },
                          backgroundColor: index % 2 === 0 ? '#fff' : '#fafafa'
                        }}
                      >
                        <TableCell>
                          <Typography variant='body2' fontWeight='500'>
                            {shipment.shipmentId || `SHIP-${index + 1}`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant='body2'>
                            {shipment.bolNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant='body2'>
                            {shipment.proNumber || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            <Avatar
                              sx={{ width: 24, height: 24, bgcolor: '#1976d2' }}
                            >
                              <CalendarToday sx={{ fontSize: 14 }} />
                            </Avatar>
                            <Box>
                              <Typography variant='body2' fontWeight='500'>
                                {shipment.formattedPickupDate}
                              </Typography>
                              <Typography
                                variant='caption'
                                color='textSecondary'
                              >
                                Pickup
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            <Avatar
                              sx={{ width: 24, height: 24, bgcolor: '#f44336' }}
                            >
                              <CalendarToday sx={{ fontSize: 14 }} />
                            </Avatar>
                            <Box>
                              <Typography variant='body2' fontWeight='500'>
                                {shipment.formattedActualDate}
                              </Typography>
                              <Typography
                                variant='caption'
                                color='textSecondary'
                              >
                                Actual
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align='center'>
                          <Chip
                            label={`${shipment.actualTransitDays} days`}
                            size='small'
                            color='primary'
                            variant='outlined'
                          />
                        </TableCell>
                        <TableCell align='center'>
                          <Chip
                            label={`${shipment.estimatedTransitDays} days`}
                            size='small'
                            color='primary'
                            variant='outlined'
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <Chip
                            label={`${shipment.daysLateBeyondGrace} days`}
                            size='small'
                            color='error'
                            variant='outlined'
                          />
                          <Typography
                            variant='caption'
                            display='block'
                            color='textSecondary'
                          >
                            {Math.round(shipment.diffHours)} hours late
                          </Typography>
                        </TableCell>
                        <TableCell align='center'>
                          <Box>
                            <Typography variant='body2'>
                              <strong>Expected:</strong>{' '}
                              {shipment.expectedTransitDays} days
                            </Typography>
                            <Typography variant='body2'>
                              <strong>Actual:</strong>{' '}
                              {shipment.actualTransitDays} days
                            </Typography>
                            <Chip
                              label={`${
                                shipment.actualTransitDays -
                                shipment.expectedTransitDays
                              } days diff`}
                              size='small'
                              color={
                                shipment.actualTransitDays >
                                shipment.expectedTransitDays
                                  ? 'error'
                                  : 'success'
                              }
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Late Shipments Summary */}
              <Paper sx={{ p: 2, mt: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Typography variant='subtitle2'>
                    Late Shipments Analysis (Based on Transit Days)
                  </Typography>
                  <Typography variant='caption' color='textSecondary'>
                    Sorted by: {lateSortColumn} ({lateSortDirection})
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant='caption' color='textSecondary'>
                      Total Late Shipments
                    </Typography>
                    <Typography variant='h6'>
                      {sortedLateShipments.length}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant='caption' color='textSecondary'>
                      Total Late Days
                    </Typography>
                    <Typography variant='h6'>
                      {sortedLateShipments.reduce(
                        (sum, s) => sum + s.daysLateBeyondGrace,
                        0
                      )}{' '}
                      days
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant='caption' color='textSecondary'>
                      Avg Transit Days
                    </Typography>
                    <Typography variant='h6'>
                      {(
                        sortedLateShipments.reduce(
                          (sum, s) => sum + s.actualTransitDays,
                          0
                        ) / sortedLateShipments.length
                      ).toFixed(1)}{' '}
                      days
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant='caption' color='textSecondary'>
                      Avg Delay
                    </Typography>
                    <Typography variant='h6' color='error.main'>
                      {(
                        sortedLateShipments.reduce(
                          (sum, s) => sum + s.daysLateBeyondGrace,
                          0
                        ) / sortedLateShipments.length
                      ).toFixed(1)}{' '}
                      days
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  variant='caption'
                  color='textSecondary'
                  sx={{ mt: 1, display: 'block' }}
                >
                  *All calculations use America/New_York timezone (EST/EDT)
                  <br />
                  *Transit Days = Business Days between Pickup and Delivery
                  <br />
                  *Weekends and US Federal holidays excluded from transit days
                  <br />
                  *Expected delivery includes 1 calendar day grace period
                  <br />
                  *On-time = Delivery on or before (Expected Date + Grace
                  Period)
                  <br />
                  *Late = Delivery after (Expected Date + Grace Period)
                  <Button
                    size='small'
                    variant='text'
                    startIcon={<Event />}
                    onClick={() => setHolidayCalendarOpen(true)}
                    sx={{ ml: 1 }}
                  >
                    View Holiday Calendar
                  </Button>
                </Typography>
              </Paper>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircle sx={{ fontSize: 48, color: '#4caf50', mb: 2 }} />
              <Typography variant='h6' color='textSecondary'>
                No Late Shipments Found
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                All shipments for {selectedCarrier} were delivered within
                transit days + 1-day grace period
              </Typography>
              <Button
                variant='outlined'
                startIcon={<Event />}
                onClick={() => setHolidayCalendarOpen(true)}
                sx={{ mt: 2 }}
              >
                Check Holiday Calendar
              </Button>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setLateShipmentsDialogOpen(false)}>
            Close
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={() =>
              exportLateShipmentsToExcel(selectedCarrier, sortedLateShipments)
            }
            disabled={!sortedLateShipments.length}
            startIcon={<Download />}
          >
            Export to Excel
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ p: 0 }}>
      {/* Dialogs */}
      <LateShipmentsDialog />
      <HolidayCalendarDialog />

      {/* Status Legend */}
      <Paper elevation={1} sx={{ p: 1.5, mb: 2, borderRadius: 1 }}>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          <Typography variant='body2' fontWeight='medium'>
            Status:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle sx={{ color: '#4caf50' }} />
            <Typography variant='caption'>Excellent (95-100%)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning sx={{ color: '#ff9800' }} />
            <Typography variant='caption'>Good (80-94%)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Error sx={{ color: '#f44336' }} />
            <Typography variant='caption'>Poor (&lt;80%)</Typography>
          </Box>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}
          >
            <Button
              size='small'
              variant='outlined'
              startIcon={<Event />}
              onClick={() => setHolidayCalendarOpen(true)}
            >
              View Holiday Calendar
            </Button>
            <Typography variant='caption' color='textSecondary' sx={{ ml: 2 }}>
              *Outbound LTL On Time Performance* (EST/EDT Timezone)
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Performance Table */}
      <Paper elevation={2} sx={{ borderRadius: 1, overflow: 'hidden' }}>
        <Box
          sx={{
            maxHeight: '55vh',
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px'
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1'
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '4px'
            }
          }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              minWidth: '900px'
            }}
          >
            <thead>
              <tr
                style={{
                  backgroundColor: '#f5f5f5',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1
                }}
              >
                {/* Carrier */}
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    width: '20%',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleSort('carrierName')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Carrier
                    {getSortIcon('carrierName')}
                  </Box>
                </th>

                {/* Total Shipments */}
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'right',
                    borderBottom: '2px solid #e0e0e0',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    width: '12%',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleSort('totalShipments')}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 0.5
                    }}
                  >
                    Total
                    {getSortIcon('totalShipments')}
                  </Box>
                </th>

                {/* % of Total */}
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'right',
                    borderBottom: '2px solid #e0e0e0',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    width: '10%',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleSort('percentOfTotal')}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 0.5
                    }}
                  >
                    % of Total
                    {getSortIcon('percentOfTotal')}
                  </Box>
                </th>

                {/* Late Shipments */}
                {lateShipsCol && (
                  <th
                    style={{
                      padding: '12px 16px',
                      textAlign: 'right',
                      borderBottom: '2px solid #e0e0e0',
                      fontWeight: '600',
                      fontSize: '0.875rem',
                      width: '12%',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleSort('lateShipments')}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 0.5
                      }}
                    >
                      Late
                      {getSortIcon('lateShipments')}
                    </Box>
                  </th>
                )}

                {/* On-Time % */}
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'right',
                    borderBottom: '2px solid #e0e0e0',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    width: '12%',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleSort('onTimePercentage')}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 0.5
                    }}
                  >
                    On-Time %{getSortIcon('onTimePercentage')}
                  </Box>
                </th>

                {/* Avg Late Days */}
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'right',
                    borderBottom: '2px solid #e0e0e0',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    width: '12%',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleSort('avgLateDays')}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 0.5
                    }}
                  >
                    Avg Late Days
                    {getSortIcon('avgLateDays')}
                  </Box>
                </th>

                {/* Status */}
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'center',
                    borderBottom: '2px solid #e0e0e0',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    width: '10%'
                  }}
                >
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {calculateOnTimeDelivery.carrierData.length > 0 ? (
                calculateOnTimeDelivery.carrierData.map((carrier, index) => (
                  <tr
                    key={carrier.carrierName}
                    style={{
                      backgroundColor: carrier.statusColor,
                      borderBottom: '1px solid #e0e0e0',
                      transition: 'background-color 0.2s',
                      '&:hover': {
                        backgroundColor:
                          carrier.status === 'green'
                            ? '#d4edda'
                            : carrier.status === 'yellow'
                              ? '#fff3cd'
                              : '#f8d7da'
                      }
                    }}
                  >
                    <td
                      style={{
                        padding: '12px 16px',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                      }}
                    >
                      {carrier.carrierName}
                    </td>
                    <td
                      style={{
                        padding: '12px 16px',
                        textAlign: 'right',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                      }}
                    >
                      {carrier.totalShipments.toLocaleString()}
                    </td>
                    <td
                      style={{
                        padding: '12px 16px',
                        textAlign: 'right',
                        fontSize: '0.875rem'
                      }}
                    >
                      {carrier.percentOfTotal}
                    </td>
                    {lateShipsCol && (
                      <td
                        style={{
                          padding: '12px 16px',
                          textAlign: 'right',
                          fontSize: '0.875rem',
                          color:
                            carrier.lateShipments > 0 ? '#d32f2f' : 'inherit'
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 1
                          }}
                        >
                          {carrier.lateShipments.toLocaleString()}
                          {carrier.lateShipments > 0 && viewDetails && (
                            <Button
                              size='small'
                              variant='outlined'
                              sx={{
                                ml: 1,
                                py: 0.25,
                                px: 1,
                                fontSize: '0.75rem',
                                minWidth: 'auto'
                              }}
                              onClick={() =>
                                handleViewLateShipments(carrier.carrierName)
                              }
                            >
                              View Details
                            </Button>
                          )}
                        </Box>
                      </td>
                    )}
                    <td
                      style={{
                        padding: '12px 16px',
                        textAlign: 'right',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color:
                          carrier.rawOnTimePercent >= 95
                            ? '#2e7d32'
                            : carrier.rawOnTimePercent >= 80
                              ? '#ed6c02'
                              : '#c62828'
                      }}
                    >
                      {carrier.onTimePercentage}
                    </td>
                    <td
                      style={{
                        padding: '12px 16px',
                        textAlign: 'right',
                        fontSize: '0.875rem'
                      }}
                    >
                      {carrier.avgLateDays}
                    </td>

                    <td
                      style={{
                        padding: '12px 16px',
                        textAlign: 'center'
                      }}
                    >
                      {carrier.icon}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={8}
                    style={{
                      padding: '40px 16px',
                      textAlign: 'center',
                      color: '#666'
                    }}
                  >
                    <Typography variant='body1'>
                      No carrier data found.
                    </Typography>
                    {searchTerm && (
                      <Typography
                        variant='body2'
                        color='textSecondary'
                        sx={{ mt: 1 }}
                      >
                        Try searching for a different carrier name.
                      </Typography>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>

        {/* Summary Footer */}
        {calculateOnTimeDelivery.summary && (
          <Box
            sx={{
              p: 1,
              backgroundColor: '#f8f9fa',
              borderTop: '2px solid #dee2e6',
              display: 'flex',
              alignItems: 'center',
              minHeight: '48px'
            }}
          >
            {/* Left side info */}
            <Box sx={{ width: '25%', pl: 2 }}>
              <Typography variant='body2' color='textSecondary'>
                SUMMARY • {selectedShipmentType} shipments
                {searchTerm && ` • "${searchTerm}"`}
              </Typography>
            </Box>

            {/* Stats aligned with columns */}
            <Box sx={{ display: 'flex', flex: 1 }}>
              {/* Total Shipments column (12%) */}
              <Box
                sx={{
                  width: '15%',
                  display: 'flex',
                  textAlign: 'right',
                  pr: 0
                }}
              >
                <Typography
                  variant='body2'
                  color='textSecondary'
                  display='block'
                >
                  Total:
                </Typography>
                <Typography variant='body2' fontWeight='600'>
                  {calculateOnTimeDelivery.summary.totalShipments}
                </Typography>
              </Box>

              {/* % of Total column (10%) */}
              <Box
                sx={{
                  width: '15%',
                  display: 'flex',
                  textAlign: 'right',
                  pr: 2
                }}
              >
                <Typography
                  variant='body2'
                  color='textSecondary'
                  display='block'
                >
                  % of Total:
                </Typography>
                <Typography variant='body2' fontWeight='600'>
                  100%
                </Typography>
              </Box>

              {/* Late Shipments column (12% - only if shown) */}
              {lateShipsCol && (
                <Box
                  sx={{
                    width: '15%',
                    display: 'flex',
                    textAlign: 'right',
                    pr: 2
                  }}
                >
                  <Typography
                    variant='body2'
                    color='textSecondary'
                    display='block'
                  >
                    Late:
                  </Typography>
                  <Typography variant='body2' fontWeight='600' color='#d32f2f'>
                    {calculateOnTimeDelivery.summary.totalLateShipments}
                  </Typography>
                </Box>
              )}

              {/* On-Time % column (12%) */}
              <Box
                sx={{
                  width: '15%',
                  display: 'flex',
                  textAlign: 'right',
                  pr: 2
                }}
              >
                <Typography
                  variant='body2'
                  color='textSecondary'
                  display='block'
                >
                  On-Time %:
                </Typography>
                <Typography
                  variant='body2'
                  fontWeight='600'
                  sx={{
                    color:
                      calculateOnTimeDelivery.summary.onTimePercentage >= 95
                        ? '#2e7d32'
                        : calculateOnTimeDelivery.summary.onTimePercentage >= 80
                          ? '#ed6c02'
                          : '#c62828'
                  }}
                >
                  {calculateOnTimeDelivery.summary.onTimePercentage}%
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Quick Stats */}
      {calculateOnTimeDelivery.carrierData.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={`${
              calculateOnTimeDelivery.carrierData.filter(
                c => c.status === 'green'
              ).length
            } Excellent`}
            size='small'
            icon={<CheckCircle />}
            sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }}
          />
          <Chip
            label={`${
              calculateOnTimeDelivery.carrierData.filter(
                c => c.status === 'yellow'
              ).length
            } Good`}
            size='small'
            icon={<Warning />}
            sx={{ backgroundColor: '#fffde7', color: '#ed6c02' }}
          />
          <Chip
            label={`${
              calculateOnTimeDelivery.carrierData.filter(
                c => c.status === 'red'
              ).length
            } Poor`}
            size='small'
            icon={<Error />}
            sx={{ backgroundColor: '#ffebee', color: '#c62828' }}
          />
          <Button
            size='small'
            variant='outlined'
            startIcon={<Event />}
            onClick={() => setHolidayCalendarOpen(true)}
            sx={{ ml: 'auto' }}
          >
            Holiday Calendar
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default OnTimeDeliveryTab;
